// #tag: APPEARANCE AND THEME CONTEXT PROVIDER
// #purpose: Manages theme modes (light, dark, system), accent palettes, typography sizing, sound effects, and accessibility preferences.
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { AppearanceSettings } from '../types';

const STORAGE_KEY = 'wfx-results-appearance';

// #tag: DEFAULT APPEARANCE PREFERENCES
// #purpose: Baseline design tokens for theme, font scale, animations, and sound volume.
const DEFAULTS: AppearanceSettings = {
  theme: 'system',
  accent: 'orange',
  fontSize: 'medium',
  compactMode: false,
  animations: true,
  soundEnabled: false,
  soundVolume: 40,
  respectReducedMotion: true,
  respectSystemSilent: true,
};

function loadSettings(): AppearanceSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return { ...DEFAULTS, ...JSON.parse(raw) };
  } catch {
    /* ignore */
  }
  return DEFAULTS;
}

// #tag: DOM THEME ATTRIBUTE APPLIER
// #purpose: Syncs active appearance settings to data-attributes and class flags on the root HTML element.
function applySettings(settings: AppearanceSettings) {
  const root = document.documentElement;
  if (settings.theme === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', settings.theme);
  root.setAttribute('data-accent', settings.accent);
  root.setAttribute('data-font', settings.fontSize);
  root.classList.toggle('compact-mode', settings.compactMode);
  root.classList.toggle('no-animations', !settings.animations);
  root.classList.toggle('no-sound', !settings.soundEnabled);
}

interface AppearanceContextValue {
  settings: AppearanceSettings;
  setSettings: (patch: Partial<AppearanceSettings>) => void;
}

const AppearanceContext = createContext<AppearanceContextValue | null>(null);

// #tag: APPEARANCE CONTEXT PROVIDER COMPONENT
// #purpose: Exposes stateful appearance controls to child components and persists changes to localStorage.
export function AppearanceProvider({ children }: { children: React.ReactNode }) {
  const [settings, setState] = useState<AppearanceSettings>(loadSettings);

  useEffect(() => {
    applySettings(settings);
  }, [settings]);

  const setSettings = useCallback((patch: Partial<AppearanceSettings>) => {
    setState((prev) => {
      const next = { ...prev, ...patch };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  return (
    <AppearanceContext.Provider value={{ settings, setSettings }}>
      {children}
    </AppearanceContext.Provider>
  );
}

// #tag: APPEARANCE PREFERENCES HOOK
// #purpose: Allows UI components to query active theme tokens and dispatch customization updates.
export function useAppearance(): AppearanceContextValue {
  const ctx = useContext(AppearanceContext);
  if (!ctx) throw new Error('useAppearance must be used within AppearanceProvider');
  return ctx;
}
